// @file All network requests used by dashboard store
import {
  Billing as BillingType,
  fetchBilling,
  fetchInvoices,
  Invoice as InvoiceType,
  JsonAPIResponse,
  JsonAPIUpdateRequest,
  Wall as WallType,
} from '@padlet/arvo'
import { HTTPMethod } from '@padlet/fetch'
import { ApolloError, MutationOptions } from 'apollo-client'
import { apolloClient } from 'bits/apollo'
import { currentHostWithProtocol, transformCurrentUrl } from 'bits/location'
import { LibraryMembershipRole, LibraryMembershipStatus } from 'enums'
import { CREATE_FOLDER, DELETE_FOLDER, UPDATE_FOLDER } from 'graphql/folders'
import { DELETE_PADLET } from 'graphql/padlets'
import template from 'lodash.template'
import { fetchJson } from 'surface/api_fetch'
import type {
  AdvertisedQuotasApiResult,
  BackpackPlanResult,
  BillingPauseResult,
  ChangeCardUrl,
  DomainValidation,
  EstimateCancellationRefundResult,
  EstimateLibrarySwitchingCostResult,
  EstimateSwitchingCostResult,
  EstimateTeamNeonDowngradeRefund,
  Folder as FolderType,
  FolderId,
  Id,
  Library as LibraryType,
  LibraryDowngradeToTeamNeonResult,
  LibraryId,
  LibraryInviteLinks,
  LibraryInvoice as LibraryInvoiceType,
  LibraryMembership,
  LibraryMembershipId,
  LibraryPlan as LibraryPlanType,
  LibrarySwitchResult,
  LibraryUserEmailDomain as LibraryUserEmailDomainType,
  NotificationSettingApiResponse,
  Plan as PlanType,
  ScheduledChangesResult,
  SwitchCurrencyResult,
  SwitchPlanResult,
  Tenant as TenantType,
  TenantAnalytics as TenantAnalyticsType,
  TenantId,
  TenantInviteLinks as TenantInviteLinksType,
  TenantInviteLinksParams,
  TenantParams,
  UndoLibraryScheduledChangeResult,
  UndoScheduledChangeResult,
  User as UserType,
  UserFollowApiResponse,
  UserId,
  WallFollowApiResponse,
  WallId,
} from 'types'

const API_VERSION = 5

const deleteWallMutationParams = (wallId: WallId): MutationOptions => {
  const mutation = DELETE_PADLET
  const variables = { wallId }
  return { mutation, variables }
}

async function deleteWallMutation(wallId: WallId): Promise<any> {
  const params = deleteWallMutationParams(wallId)
  return await apolloClient.mutate(params)
}

const createFolderMutationParams = (name: string): MutationOptions => {
  const mutation = CREATE_FOLDER
  const variables = { name }
  return { mutation, variables }
}

async function createFolderMutation(name: string): Promise<any> {
  const params = createFolderMutationParams(name)
  return await apolloClient.mutate(params)
}

const updateFolderMutationParams = ({ name, folderId }: { name: string; folderId: FolderId }): MutationOptions => {
  const mutation = UPDATE_FOLDER
  const variables = { name, folderId }
  return { mutation, variables }
}

async function updateFolderMutation({ name, folderId }: { name: string; folderId: FolderId }): Promise<any> {
  const params = updateFolderMutationParams({ name, folderId })
  return await apolloClient.mutate(params)
}

const deleteFolderMutationParams = (folderId: FolderId): MutationOptions => {
  const mutation = DELETE_FOLDER
  const variables = { folderId }
  return { mutation, variables }
}

async function deleteFolderMutation(folderId: FolderId): Promise<any> {
  const params = deleteFolderMutationParams(folderId)
  return await apolloClient.mutate(params)
}

function fetchFirstGraphQLError(e: unknown): null | string {
  if (!e) return null
  return (e as ApolloError)?.graphQLErrors?.[0]?.message
}

class Billing {
  public static async fetch(): Promise<JsonAPIResponse<BillingType>> {
    return await fetchBilling()
  }

  public static async pause(fetchOptions = {}): Promise<BillingPauseResult> {
    // note: not a JSON:API formatted api call
    return await fetchJson('/premium/pause', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async resume(fetchOptions = {}): Promise<BillingPauseResult> {
    // note: not a JSON:API formatted api call
    return await fetchJson('/premium/resume', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  /**
   * Fetch estimated refund.
   */
  public static async estimateCancellationRefund(
    fetchOptions = {},
  ): Promise<JsonAPIResponse<EstimateCancellationRefundResult>> {
    return await fetchJson(`/premium/cancel/refund_estimate`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async cancelPause(fetchOptions = {}): Promise<BillingPauseResult> {
    // note: not a JSON:API formatted api call
    return await fetchJson('/premium/cancel_pausing', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async undoScheduledCancellation(fetchOptions = {}): Promise<UndoScheduledChangeResult> {
    return await fetchJson('/premium/undo_scheduled_cancellation', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async undoScheduledSwitch(fetchOptions = {}): Promise<UndoScheduledChangeResult> {
    return await fetchJson('/premium/undo_scheduled_plan_switch', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async estimateSwitchingCost(fetchOptions = {}): Promise<EstimateSwitchingCostResult> {
    return await fetchJson('/premium/switch_plan/estimate_cost', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async switchPlan(fetchOptions = {}): Promise<SwitchPlanResult> {
    return await fetchJson('/premium/switch_plan', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async switchCurrency(fetchOptions = {}): Promise<SwitchCurrencyResult> {
    return await fetchJson('/premium/switch_currency', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async cancelMembership(fetchOptions = {}): Promise<SwitchPlanResult> {
    return await fetchJson('/premium/cancel', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async fetchScheduledChanges(): Promise<ScheduledChangesResult> {
    return await fetchJson('/api/3/billing/scheduled_changes', {
      method: HTTPMethod.get,
    })
  }
}

class Invoice {
  public static async fetch(): Promise<JsonAPIResponse<InvoiceType>> {
    return await fetchInvoices()
  }
}

class Plan {
  /**
   * Fetch list of available plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<PlanType>> {
    return await fetchJson(`/api/${API_VERSION}/plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchAdvertisedQuotas(fetchOptions = {}): Promise<AdvertisedQuotasApiResult> {
    return await fetchJson('/api/3/membership_tier_quotas', {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class LibraryPermissions {
  public static async fetch(id: LibraryId): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/1/libraries/${id}/school_settings`, {
      method: HTTPMethod.get,
    })
  }
}

class LibraryBilling {
  /**
   * Fetch library billing information.
   */
  public static async fetch({ libraryId }: { libraryId: LibraryId }, fetchOptions = {}): Promise<JsonAPIResponse<any>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  /**
   * Cancel library subscription.
   */
  public static async cancelSubscription(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryDowngradeToTeamNeonResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/cancel`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  /**
   * Fetch estimated downgrade to Team Neon refund.
   */
  public static async estimateTeamNeonDowngradeRefund(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<EstimateTeamNeonDowngradeRefund>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/cancel/refund_estimate`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  /**
   * Fetch estimated downgrade to Team Neon refund.
   */
  public static async libraryChangeCardUrl(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<ChangeCardUrl>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/change_card_url`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  /**
   * Undo a scheduled downgrade to Team Neon.
   */
  public static async undoScheduledDowngradeToTeamNeon(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<UndoLibraryScheduledChangeResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/undo_scheduled_cancellation`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  /**
   * Undo a scheduled crossgrade.
   */
  public static async undoScheduledCrossgrade(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<UndoLibraryScheduledChangeResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/undo_scheduled_plan_switch`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async estimateSwitchingCost(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<EstimateLibrarySwitchingCostResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/switch_plan/estimate_cost`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async switchPlan(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibrarySwitchResult>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/switch_plan`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }
}

class LibraryInvoice {
  public static async fetch(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryInvoiceType>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}/billing/invoices`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class LibraryPlan {
  /**
   * Fetch list of available library plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<LibraryPlanType>> {
    return await fetchJson(`/api/${API_VERSION}/team_plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  /**
   * Fetch list of available library plans for current user and a specific library (respective to the user's country code/currency)
   */
  public static async fetchByLibraryId(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryPlanType>> {
    return await fetchJson(`/api/${API_VERSION}/team_plans?library_id=${libraryId}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class Library {
  public static get libraryUrl(): string {
    return `/api/1/libraries?userId=\${ userId }&filter=\${ filter }`
  }

  public static buildUrl(url, options): string {
    return template(url)({ ...options, interpolate: /\${([\s\S]+?)}/g })
  }

  public static async fetchCreatableLibraries(
    { userId }: { userId: UserId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'wall_creatable' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchViewableLibraries(
    { userId }: { userId: UserId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'all_walls_viewable' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async delete(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/${API_VERSION}/libraries/${libraryId}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }
}

class LibraryInfo {
  public static async fetch(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/info`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async update(
    {
      libraryId,
      updateAttributes,
    }: { libraryId: LibraryId; updateAttributes: { avatar?: string; name?: string; slug?: string; bio?: string } },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/info`, {
      method: HTTPMethod.patch,
      jsonData: {
        data: {
          attributes: updateAttributes,
        },
      },
      ...fetchOptions,
    })
  }
}

class LibraryUserEmailDomain {
  public static async fetch(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryUserEmailDomainType>> {
    return await fetchJson(`/api/1/libraries/${libraryId}/user_domains`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class LibraryMembers {
  public static async fetchMemberships(
    { libraryId, next }: { libraryId: LibraryId; next: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryMembership>> {
    const url = transformCurrentUrl(
      {},
      {
        path: '/api/1/libraries/memberships',
        search: {
          library_id: String(libraryId),
          next,
        },
      },
    )
    return await fetchJson(url, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async updateMembership(
    { id, role, status }: { id: LibraryMembershipId; role: LibraryMembershipRole; status: LibraryMembershipStatus },
    fetchOptions = {},
  ): Promise<void> {
    return await fetchJson('/api/1/libraries/memberships', {
      method: HTTPMethod.put,
      jsonData: {
        data: {
          attributes: {
            id,
            role,
            status,
          },
        },
      },
      ...fetchOptions,
    })
  }

  public static async changeOwner(
    { libraryId, userId }: { libraryId: LibraryId; userId: UserId },
    fetchOptions = {},
  ): Promise<void> {
    return await fetchJson(`/api/1/libraries/${libraryId}/change_owner`, {
      method: HTTPMethod.post,
      jsonData: {
        userId,
      },
      ...fetchOptions,
    })
  }

  public static async leaveLibrary({ libraryId }: { libraryId: LibraryId }, fetchOptions = {}): Promise<void> {
    return await fetchJson('/api/1/libraries/leave', {
      method: HTTPMethod.post,
      jsonData: {
        libraryId,
      },
      ...fetchOptions,
    })
  }

  public static async fetchLibraryInviteLinks(
    { libraryId }: { libraryId: LibraryId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryInviteLinks>> {
    return await fetchJson(`/api/1/libraries/invite_links?library_id=${libraryId}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async toggleInviteLink(
    updateAttributes: {
      libraryId: LibraryId
      codeForAdminEnabled?: boolean
      codeForMakerEnabled?: boolean
      codeForContributorEnabled?: boolean
    },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryInviteLinks>> {
    return await fetchJson('/api/1/libraries/invite_links', {
      method: HTTPMethod.put,
      jsonData: {
        data: {
          attributes: updateAttributes,
        },
      },
      ...fetchOptions,
    })
  }

  public static async search(
    {
      libraryId,
      query,
      next,
    }: {
      libraryId: LibraryId
      query: string
      next: string
    },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<LibraryMembership>> {
    const url = transformCurrentUrl(
      {},
      {
        path: '/api/1/libraries/memberships/search',
        search: {
          library_id: String(libraryId),
          q: query,
          next,
        },
      },
    )
    return await fetchJson(url, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class BackpackPlan {
  /**
   * Fetch list of available backpack plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<BackpackPlanResult>> {
    return await fetchJson(`/api/${API_VERSION}/backpack_plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class User {
  public static async updatePassword(fetchOptions = {}): Promise<JsonAPIResponse<UserType>> {
    return await fetchJson(`/api/1/dashboard_settings/password`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async fetchSettingsUserInfo(fetchOptions = {}): Promise<JsonAPIResponse<UserType>> {
    return await fetchJson(`/api/1/dashboard_settings/user`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async updateSettingsUserInfo(fetchOptions = {}): Promise<JsonAPIResponse<UserType>> {
    return await fetchJson(`/api/1/dashboard_settings/user`, {
      method: HTTPMethod.patch,
      ...fetchOptions,
    })
  }

  public static async updateBasicInfo(id: UserId, fetchOptions = {}): Promise<JsonAPIResponse<UserType>> {
    return await fetchJson(`/users/${id}`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async fetchUserWalls(id: UserId, fetchOptions = {}): Promise<WallType[]> {
    return await fetchJson(`/users/${id}/walls`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchCommunicationSettings(
    id,
    fetchOptions = {},
  ): Promise<{ communication: { wall_create: boolean; product_update: boolean } }> {
    return await fetchJson(`/users/${id}/settings`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async updateCommunicationSettings(
    id,
    fetchOptions = {},
  ): Promise<JsonAPIResponse<{ communication: { wall_create: boolean; product_update: boolean } }>> {
    return await fetchJson(`/users/${id}/settings`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async toggleAllNotifications(fetchOptions = {}): Promise<{ notify_all: boolean }> {
    return await fetchJson(`${currentHostWithProtocol()}/_/notifications`, {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }
}

class Tenant {
  public static async updateTenantInfo(
    id: TenantId,
    fetchOptions: Partial<TenantParams>,
  ): Promise<JsonAPIResponse<TenantType>> {
    const body: string = JSON.stringify(fetchOptions)
    return await fetchJson(`/tenants/${id}`, {
      method: HTTPMethod.put,
      body,
    })
  }

  public static async isSubdomainValid(subdomain: string): Promise<DomainValidation> {
    return await fetchJson(
      transformCurrentUrl(
        {},
        {
          path: '/tenants/subdomain_valid',
          search: { subdomain },
        },
      ),
      {
        method: HTTPMethod.get,
      },
    )
  }
}

class TenantInviteLinks {
  public static async get(id: TenantId): Promise<JsonAPIResponse<TenantInviteLinksType>> {
    return await fetchJson(`/api/tenants/${id}/invite_links`, {
      method: HTTPMethod.get,
    })
  }

  public static async patch(
    id: TenantId,
    fetchOptions: JsonAPIUpdateRequest<Partial<TenantInviteLinksParams>>,
  ): Promise<JsonAPIResponse<TenantInviteLinksType>> {
    const body: string = JSON.stringify(fetchOptions)
    return await fetchJson(`/api/tenants/${id}/invite_links`, {
      method: HTTPMethod.patch,
      body,
    })
  }
}

class TenantPermissions {
  public static async fetch(id: TenantId): Promise<JsonAPIResponse<TenantType>> {
    return await fetchJson(`/api/1/tenants/${id}/permissions`, {
      method: HTTPMethod.get,
    })
  }

  public static async update(id: TenantId, tenant: Partial<TenantType>): Promise<JsonAPIResponse<TenantType>> {
    return await fetchJson(`/api/1/tenants/${id}/permissions`, {
      method: HTTPMethod.patch,
      body: JSON.stringify({ data: { type: 'tenant', id: tenant.id, attributes: tenant } }),
    })
  }
}

class TenantAnalytics {
  public static async fetch(id: TenantId): Promise<JsonAPIResponse<TenantAnalyticsType>> {
    return await fetchJson(`/api/${API_VERSION}/tenants/${id}/analytics`, {
      method: HTTPMethod.get,
    })
  }
}

class Lti {
  public static async generateLtiDynamicRegistrationUrl(id: TenantId): Promise<{ url: string; tenantId: number }> {
    return await fetchJson(`/api/lti/dynamic_registration_url?id=${id}`, {
      method: HTTPMethod.get,
    })
  }

  public static async generateLtiLinkDeploymentToken(id: TenantId): Promise<{ token: string; tenantId: number }> {
    return await fetchJson(`/api/lti/link_deployment_token?id=${id}`, {
      method: HTTPMethod.get,
    })
  }
}

class NotificationsSettings {
  public static async fetch(): Promise<JsonAPIResponse<NotificationSettingApiResponse>> {
    return await fetchJson(`/api/${API_VERSION}/notification_settings/general`, {
      method: HTTPMethod.get,
    })
  }

  public static async update(fetchOptions = {}): Promise<JsonAPIResponse<NotificationSettingApiResponse>> {
    return await fetchJson(`/api/${API_VERSION}/notification_settings/general`, {
      method: HTTPMethod.patch,
      ...fetchOptions,
    })
  }
}

class UserFollows {
  public static async fetch({
    pageNumber,
    pageSize,
  }: {
    pageNumber: number
    pageSize: number
  }): Promise<JsonAPIResponse<UserFollowApiResponse>> {
    return await fetchJson(
      transformCurrentUrl(
        {},
        {
          path: `/api/${API_VERSION}/notification_settings/user_follows`,
          search: { 'page[size]': String(pageSize), 'page[number]': String(pageNumber) },
        },
      ),
      {
        method: HTTPMethod.get,
      },
    )
  }

  public static async create(fetchOptions = {}): Promise<{ success: boolean }> {
    return await fetchJson('/api/user_follows.json', {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async delete(fetchOptions = {}): Promise<{ success: boolean }> {
    return await fetchJson('/api/user_follows.json', {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }
}

class WallFollows {
  public static async fetch({
    pageNumber,
    pageSize,
  }: {
    pageNumber: number
    pageSize: number
  }): Promise<JsonAPIResponse<WallFollowApiResponse>> {
    return await fetchJson(
      transformCurrentUrl(
        {},
        {
          path: `/api/${API_VERSION}/notification_settings/wall_follows`,
          search: { 'page[size]': String(pageSize), 'page[number]': String(pageNumber) },
        },
      ),
      {
        method: HTTPMethod.get,
      },
    )
  }

  public static async create(fetchOptions = {}): Promise<JsonAPIResponse<WallFollowApiResponse>> {
    return await fetchJson(`/api/${API_VERSION}/notification_settings/wall_follows`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }

  public static async update(
    { wallFollowId }: { wallFollowId: Id },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<WallFollowApiResponse>> {
    return await fetchJson(`/api/${API_VERSION}/notification_settings/wall_follows/${String(wallFollowId)}`, {
      method: HTTPMethod.patch,
      ...fetchOptions,
    })
  }
}

class Wall {
  public static async archive(wallId: number): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/archive`, {
      method: HTTPMethod.put,
    })
  }

  public static async unarchive(wallId: number): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/unarchive`, {
      method: HTTPMethod.put,
    })
  }

  public static async delete(wallId: number): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}`, {
      method: HTTPMethod.delete,
    })
  }
}

class Folder {
  public static async create(attributes: Pick<FolderType, 'name'>): Promise<JsonAPIResponse<FolderType>> {
    return await fetchJson(`/api/${API_VERSION}/folders`, {
      method: HTTPMethod.post,
      jsonData: {
        data: {
          type: 'folder',
          attributes,
        },
      },
    })
  }

  public static async delete(folderId: FolderType['id']): Promise<JsonAPIResponse<FolderType>> {
    return await fetchJson(`/api/${API_VERSION}/folders/${folderId}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async update(
    folderId: FolderType['id'],
    attributes: Pick<FolderType, 'name'>,
  ): Promise<JsonAPIResponse<FolderType>> {
    return await fetchJson(`/api/${API_VERSION}/folders/${folderId}`, {
      method: HTTPMethod.patch,
      jsonData: {
        data: {
          type: 'folder',
          attributes,
        },
      },
    })
  }
}

export {
  deleteWallMutation,
  createFolderMutation,
  updateFolderMutation,
  deleteFolderMutation,
  fetchFirstGraphQLError,
  Billing,
  Folder,
  Invoice,
  Library,
  LibraryBilling,
  LibraryInvoice,
  LibraryPlan,
  LibraryInfo,
  LibraryMembers,
  LibraryUserEmailDomain,
  LibraryPermissions,
  Plan,
  BackpackPlan,
  User,
  Tenant,
  TenantInviteLinks,
  TenantPermissions,
  TenantAnalytics,
  Lti,
  NotificationsSettings,
  UserFollows,
  WallFollows,
  Wall,
}
